// engine-exception: quote builder — a sectioned line editor with product prefill, selection rules, drag-reorder and live per-interval totals; not a generic detail screen
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { DndContext, KeyboardSensor, PointerSensor, closestCenter, useSensor, useSensors, type DragEndEvent } from '@dnd-kit/core';
import { SortableContext, arrayMove, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useApi } from '../../engine/api/context';
import { ApiError } from '../../engine/api/client';
import { useInvalidateResource, useMeta, useResource, useResourceList, useUpdate } from '../../engine/api/hooks';
import { Button, IconButton } from '../../engine/design/primitives/Button';
import { Combobox, type ComboOption } from '../../engine/design/primitives/Combobox';
import { Field, Input, Select, Switch } from '../../engine/design/primitives/Input';
import { Badge, Banner, ErrorBanner, LifecycleRail, Money, PageHeader, Skeleton, StatusBadge, type LifecycleStep, type Tone } from '../../engine/design/primitives/Misc';
import { Dialog, DropdownMenu, useConfirm, type MenuItemDef } from '../../engine/design/primitives/Overlay';
import { Tabs } from '../../engine/design/primitives/Tabs';
import { useToast } from '../../engine/design/primitives/Toast';
import { Icon } from '../../engine/design/icons';
import { MoneyEditor } from '../../engine/fields/registry';
import { RelationPicker } from '../../engine/fields/RelationPicker';
import { RichTextEditor } from '../../engine/fields/RichText';
import type { FieldMeta } from '../../engine/meta/types';
import { useInspector } from '../../engine/views/Inspector';
import { formatDate, formatDateTime, formatQty, formatRelativeDays } from '../../lib/format';
import { computeTotals, INTERVALS, INTERVAL_LABEL, INTERVAL_SHORT, minor, type Interval, type TotalsLine } from './totals';

// The quote builder (docs/modules/quoting.md §Screens). Everything money-related is integer
// minor units: the editor emits minor, the totals panel mirrors the DB trigger live, and the
// server's totals replace the mirror on every refetch. Content edits are only offered while
// the quote is a draft — the API and the DB refuse them afterwards regardless.

interface Line extends TotalsLine {
  description: string;
  product_id: string | null;
  product_name: string | null;
  tax_ref_id: string | null;
  tax_rate_percent: string | null;
  is_optional: boolean;
  sort_order: number;
  line_total_minor: string | null;
  unit_cost_minor: string;
}
interface Section {
  id: string;
  title: string;
  description: string | null;
  selection: 'all' | 'single' | 'multi';
  is_optional: boolean;
  sort_order: number;
  lines: Line[];
}
interface Doc {
  id: string;
  ref: string;
  revision: number;
  parent_quote_id: string | null;
  title: string;
  status: string;
  currency: string;
  client_id: string;
  contact_id: string | null;
  site_id: string | null;
  owner_tech_id: string | null;
  client_name: string | null;
  contact_name: string | null;
  contact_email: string | null;
  site_name: string | null;
  owner_name: string | null;
  expires_at: string | null;
  sent_at: string | null;
  accepted_at: string | null;
  accepted_by: { name?: string; email?: string; ip?: string } | null;
  countersigned_at: string | null;
  requires_countersign: boolean;
  converted_invoice_id: string | null;
  converted_contract_id: string | null;
  discount_type: 'none' | 'percent' | 'amount';
  discount_value: string;
  tax_mode: 'exclusive' | 'inclusive' | 'none';
  intro: string | null;
  terms: string | null;
  notes: string | null;
  totals: Record<string, unknown>;
  sections: Section[];
  lines: Line[];
  tokens: { id: string; kind: string; expires_at: string; used_at: string | null; revoked_at: string | null; sent_to_email: string | null; created_at: string }[];
}

const STEPS = ['draft', 'sent', 'viewed', 'accepted', 'converted'];
const TERMINAL = new Set(['declined', 'expired', 'revised']);
const TONES: Record<string, Tone> = { draft: 'muted', sent: 'info', viewed: 'info', accepted: 'pos', converted: 'pos', declined: 'neg', expired: 'warn', revised: 'muted' };

function steps(status: string): LifecycleStep[] {
  if (TERMINAL.has(status)) return [{ label: 'Draft', state: 'done' }, { label: 'Sent', state: 'done' }, { label: status[0]!.toUpperCase() + status.slice(1), state: 'terminal' }];
  const idx = STEPS.indexOf(status);
  return STEPS.map((s, i) => ({ label: s[0]!.toUpperCase() + s.slice(1), state: i < idx ? 'done' : i === idx ? 'current' : 'upcoming' }));
}

const PRODUCT_FIELD: FieldMeta = { name: 'product_id', label: 'Product', type: 'relation', required: false, readOnly: false, nullable: true, filterable: false, sortable: false, searchable: false, inCreate: true, inUpdate: true, inList: false, inDetail: false, relation: { resource: 'products', displayField: 'name' } };
const PRICE_FIELD: FieldMeta = { ...PRODUCT_FIELD, name: 'unit_price_minor', label: 'Unit price', type: 'money', relation: undefined };

export function QuoteBuilderPage() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const api = useApi();
  const toast = useToast();
  const confirm = useConfirm();
  const invalidate = useInvalidateResource();
  const inspector = useInspector();
  const meta = useMeta('quotes');
  const docQ = useResource<Doc>('quotes', id);
  const doc = docQ.data;
  const update = useUpdate<Doc>('quotes', { toast: { success: false }, undo: false });
  const [sentLink, setSentLink] = useState<string | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [convertResult, setConvertResult] = useState<{ invoice_id: string | null; contract_ids: string[] } | null>(null);
  const refresh = useCallback(() => invalidate('quotes', id), [invalidate, id]);

  // ── lifecycle actions ─────────────────────────────────────────────────────
  const act = useMutation({
    mutationFn: ({ name, body }: { name: string; body?: unknown }) => api.request<Record<string, unknown>>(`/v1/quotes/${id}/${name}`, { method: 'POST', body: body ?? {} }),
    onSuccess: (r, v) => {
      refresh();
      if (v.name === 'send') {
        setSentLink(String(r.public_url ?? ''));
        toast({ title: 'Quote sent', detail: 'The customer link was emailed and is shown here once.' });
      } else if (v.name === 'revise') {
        toast({ title: `Revision ${String((r as { revision?: number }).revision ?? '')} created` });
        navigate(`/quotes/${String(r.id)}`);
      } else if (v.name === 'convert') {
        const inv = (r.invoice as { invoice?: { id: string } } | null)?.invoice?.id ?? null;
        setConvertResult({ invoice_id: inv, contract_ids: (r.contract_ids as string[]) ?? [] });
        invalidate('invoices');
        invalidate('contracts');
      } else toast({ title: `${v.name[0]!.toUpperCase()}${v.name.slice(1)} — done` });
    },
    onError: (e: unknown, v) => toast({ kind: 'error', title: `${v.name} failed`, detail: e instanceof ApiError ? e.detail : String(e) }),
  });
  const run = async (name: string, spec?: { title: string; body: ReactNode; confirmLabel: string; danger?: boolean; reason?: 'optional' | 'required' }, body?: Record<string, unknown>) => {
    if (spec) {
      const r = await confirm(spec);
      if (r === false) return;
      act.mutate({ name, body: { ...(body ?? {}), ...(spec.reason && r ? { reason: r } : {}) } });
      return;
    }
    act.mutate({ name, body });
  };
  const openPreview = async () => {
    try {
      const r = await api.request<{ html: string }>(`/v1/quotes/${id}/preview`, { query: { format: 'json' } });
      setPreview(r.html);
    } catch (e) {
      toast({ kind: 'error', title: 'Preview failed', detail: e instanceof ApiError ? e.detail : String(e) });
    }
  };
  const openInspector = () => {
    if (!doc) return;
    inspector.show({ key: `quote:${doc.id}`, title: doc.ref, subtitle: 'Timeline, revisions, links', content: <QuoteInspector id={doc.id} doc={doc} /> });
  };

  if (docQ.error) return <ErrorBanner error={docQ.error} action={<Button size="sm" variant="ghost" onClick={() => navigate('/quotes')}>Back</Button>} />;
  if (!doc) {
    return (
      <div className="hf-page">
        <Skeleton height={28} width={260} />
        <div style={{ marginTop: 16 }}><Skeleton lines={8} /></div>
      </div>
    );
  }

  const editable = doc.status === 'draft';
  const open = doc.status === 'sent' || doc.status === 'viewed';
  const canConvert = doc.status === 'accepted' && (!doc.requires_countersign || Boolean(doc.countersigned_at));
  const expiryRel = formatRelativeDays(doc.expires_at);
  const expired = doc.expires_at ? new Date(doc.expires_at).getTime() < Date.now() : false;

  const primary: ReactNode[] = [];
  if (editable) primary.push(<Button key="send" variant="primary" icon="send" busy={act.isPending} onClick={() => run('send', { title: `Send ${doc.ref} to ${doc.contact_name ?? doc.client_name ?? 'the customer'}?`, body: 'The quote is emailed with an accept link. Its content is frozen once sent; changes need a new revision.', confirmLabel: 'Send quote' })}>Send</Button>);
  if (doc.status === 'accepted' && doc.requires_countersign && !doc.countersigned_at) primary.push(<Button key="cs" variant="primary" icon="pencil" busy={act.isPending} onClick={() => run('countersign', { title: 'Countersign this quote?', body: 'Records your signature and unlocks conversion into an invoice/contract.', confirmLabel: 'Countersign' })}>Countersign</Button>);
  if (canConvert) primary.push(<Button key="convert" variant="primary" icon="receipt" busy={act.isPending} onClick={() => run('convert', { title: 'Convert to invoice / contract?', body: 'Creates a DRAFT invoice for the one-off lines and a contract per recurring interval. Finance reviews and issues the invoice.', confirmLabel: 'Convert' })}>Convert</Button>);
  if (open || TERMINAL.has(doc.status)) {
    if (doc.status !== 'revised') primary.push(<Button key="revise" icon="git-branch" busy={act.isPending} onClick={() => run('revise', { title: 'Create a new revision?', body: 'A new draft with the same reference (revision +1) is created; this one is superseded and its links stop working.', confirmLabel: 'Create revision' })}>Revise</Button>);
  }
  const more: MenuItemDef[] = [
    { label: 'Preview / print', icon: 'file', onSelect: () => void openPreview() },
    { label: 'Timeline & links', icon: 'history', onSelect: openInspector },
    ...(open ? ([
      { separator: true, label: '' },
      { label: 'Send reminder', icon: 'bell', onSelect: () => void run('remind') },
      { label: 'Mark declined', icon: 'x', danger: true, onSelect: () => void run('decline', { title: 'Mark as declined?', body: 'Records that the customer declined (out of band).', confirmLabel: 'Mark declined', danger: true, reason: 'optional' }) },
      { label: 'Expire now', icon: 'clock', danger: true, onSelect: () => void run('expire', { title: 'Expire this quote now?', body: 'The customer link stops working; you can still create a revision.', confirmLabel: 'Expire', danger: true }, { force: true }) },
    ] as MenuItemDef[]) : []),
    ...(doc.converted_invoice_id ? ([{ separator: true, label: '' }, { label: 'Open invoice', icon: 'receipt', onSelect: () => navigate(`/invoices/${doc.converted_invoice_id}`) }] as MenuItemDef[]) : []),
    ...(doc.converted_contract_id ? ([{ label: 'Open contract', icon: 'file-check', onSelect: () => navigate(`/contracts/${doc.converted_contract_id}`) }] as MenuItemDef[]) : []),
  ];

  return (
    <div className="hf-page">
      <PageHeader
        compact
        breadcrumb={[{ label: 'Quotes', to: '/quotes' }, { label: doc.ref }]}
        eyebrow="quoting"
        title={<span className="hf-row" style={{ gap: 10 }}><span className="hf-mono">{doc.ref}</span>{doc.revision > 1 && <span className="hf-muted" style={{ fontSize: 'var(--t-14)' }}>rev {doc.revision}</span>}</span>}
        badge={<StatusBadge status={doc.status} tones={TONES} />}
        subtitle={
          <span className="hf-row" style={{ gap: 12, flexWrap: 'wrap' }}>
            <span>{doc.client_name ?? '—'}{doc.contact_name ? ` · ${doc.contact_name}` : ''}</span>
            {expiryRel && <span className={expired && open ? 'hf-money neg' : 'hf-muted'} title={doc.expires_at ?? undefined}><Icon name="clock" size={12} /> {expired ? `expired ${expiryRel}` : `expires ${expiryRel}`}</span>}
            {doc.accepted_by?.name && <span className="hf-muted"><Icon name="pencil" size={12} /> accepted by {doc.accepted_by.name}</span>}
          </span>
        }
        actions={
          <>
            {primary}
            <DropdownMenu aria-label="More actions" trigger={<Button icon="more-horizontal" aria-label="More actions" />} items={more} />
          </>
        }
      />
      <LifecycleRail steps={steps(doc.status)} />
      {doc.status === 'accepted' && doc.requires_countersign && !doc.countersigned_at && <Banner tone="warn">Accepted by the customer — waiting for an agent countersignature before it can be converted.</Banner>}
      {doc.status === 'revised' && <Banner tone="info">Superseded by a newer revision. <Button size="sm" variant="link" onClick={() => void api.request<{ data: { id: string; revision: number }[] }>(`/v1/quotes/${id}/chain`).then((c) => navigate(`/quotes/${c.data[c.data.length - 1]!.id}`))}>Open the latest revision</Button></Banner>}
      {sentLink && (
        <Banner tone="pos" action={<Button size="sm" onClick={() => void navigator.clipboard?.writeText(sentLink).then(() => toast({ title: 'Link copied' }))}>Copy link</Button>}>
          Customer link (shown once): <span className="hf-mono" style={{ overflowWrap: 'anywhere' }}>{sentLink}</span>
        </Banner>
      )}

      <div className="hf-quote-builder">
        <div className="hf-stack" style={{ minWidth: 0 }}>
          <HeaderFields doc={doc} editable={editable} metaFields={meta.data?.fields ?? []} onSave={(body) => update.mutateAsync({ id: doc.id, body }).then(refresh)} />
          <SectionsEditor doc={doc} editable={editable} onChanged={refresh} />
          <section aria-label="Introduction">
            <h2 className="hf-section-title">Introduction <small>shown above the lines</small></h2>
            <RichTextAutosave value={doc.intro} disabled={!editable} ariaLabel="Introduction" onSave={(html) => update.mutateAsync({ id: doc.id, body: { intro: html } }).then(refresh)} />
          </section>
          <section aria-label="Terms">
            <h2 className="hf-section-title">Terms <small>accepted with a checkbox</small></h2>
            <RichTextAutosave value={doc.terms} disabled={!editable} ariaLabel="Terms" onSave={(html) => update.mutateAsync({ id: doc.id, body: { terms: html } }).then(refresh)} />
          </section>
        </div>
        <TotalsPanel doc={doc} editable={editable} onSave={(body) => update.mutateAsync({ id: doc.id, body }).then(refresh)} />
      </div>

      <Dialog open={preview !== null} onClose={() => setPreview(null)} title={`${doc.ref} — preview`} size="xl">
        {preview !== null && <iframe title="Quote preview" srcDoc={preview} style={{ width: '100%', height: '70vh', border: 0, background: 'white', borderRadius: 'var(--r-2)' }} />}
        <div className="hf-form-actions">
          <Button variant="ghost" onClick={() => setPreview(null)}>Close</Button>
          <Button icon="file" onClick={() => { const w = window.open('', '_blank'); if (w && preview) { w.document.write(preview); w.document.close(); w.focus(); w.print(); } }}>Print</Button>
        </div>
      </Dialog>
      <Dialog open={convertResult !== null} onClose={() => setConvertResult(null)} title="Converted">
        <div className="hf-stack">
          <p>The accepted quote is now in the finance core.</p>
          {convertResult?.invoice_id && <Button icon="receipt" onClick={() => navigate(`/invoices/${convertResult.invoice_id}`)}>Open the draft invoice</Button>}
          {convertResult?.contract_ids.map((c, i) => <Button key={c} icon="file-check" onClick={() => navigate(`/contracts/${c}`)}>Open contract {convertResult.contract_ids.length > 1 ? i + 1 : ''}</Button>)}
          {!convertResult?.invoice_id && !convertResult?.contract_ids.length && <span className="hf-muted">Nothing to bill: the quote had no selected lines.</span>}
        </div>
      </Dialog>
    </div>
  );
}

// ── header fields ─────────────────────────────────────────────────────────────
function HeaderFields({ doc, editable, metaFields, onSave }: { doc: Doc; editable: boolean; metaFields: FieldMeta[]; onSave: (body: Record<string, unknown>) => Promise<unknown> }) {
  const [title, setTitle] = useState(doc.title);
  const [expires, setExpires] = useState(doc.expires_at ? doc.expires_at.slice(0, 10) : '');
  useEffect(() => { setTitle(doc.title); setExpires(doc.expires_at ? doc.expires_at.slice(0, 10) : ''); }, [doc.title, doc.expires_at]);
  const f = (name: string, fallbackResource: string, filter?: Record<string, unknown>): FieldMeta => {
    const m = metaFields.find((x) => x.name === name);
    const base: FieldMeta = m ?? { ...PRODUCT_FIELD, name, label: name.replace(/_id$/, '').replace(/_/g, ' '), relation: { resource: fallbackResource, displayField: 'name' } };
    return { ...base, label: base.label[0]!.toUpperCase() + base.label.slice(1), relation: { resource: base.relation?.resource ?? fallbackResource, displayField: base.relation?.displayField ?? 'name', filter } };
  };
  if (!editable) {
    return (
      <dl className="hf-kv" aria-label="Quote header">
        <div><dt>Title</dt><dd>{doc.title}</dd></div>
        <div><dt>Client</dt><dd>{doc.client_name ?? '—'}</dd></div>
        <div><dt>Contact</dt><dd>{doc.contact_name ? `${doc.contact_name}${doc.contact_email ? ` · ${doc.contact_email}` : ''}` : '—'}</dd></div>
        <div><dt>Site</dt><dd>{doc.site_name ?? '—'}</dd></div>
        <div><dt>Owner</dt><dd>{doc.owner_name ?? '—'}</dd></div>
        <div><dt>Valid until</dt><dd>{formatDate(doc.expires_at)}</dd></div>
        {doc.sent_at && <div><dt>Sent</dt><dd>{formatDateTime(doc.sent_at)}</dd></div>}
        {doc.accepted_at && <div><dt>Accepted</dt><dd>{formatDateTime(doc.accepted_at)}{doc.accepted_by?.ip ? ` · from ${doc.accepted_by.ip}` : ''}</dd></div>}
      </dl>
    );
  }
  return (
    <div className="hf-form-grid" aria-label="Quote header">
      <Field label="Title" span={2}>
        <Input value={title} onChange={(e) => setTitle(e.target.value)} onBlur={() => { if (title.trim() && title !== doc.title) void onSave({ title: title.trim() }); }} />
      </Field>
      <Field label="Client">
        <RelationPicker field={f('client_id', 'clients')} value={doc.client_id} onChange={(v) => v && v !== doc.client_id && void onSave({ client_id: v, contact_id: null, site_id: null })} />
      </Field>
      <Field label="Contact">
        <RelationPicker field={f('contact_id', 'contacts', { client_id: doc.client_id })} value={doc.contact_id} onChange={(v) => void onSave({ contact_id: v })} />
      </Field>
      <Field label="Site">
        <RelationPicker field={f('site_id', 'sites', { client_id: doc.client_id })} value={doc.site_id} onChange={(v) => void onSave({ site_id: v })} />
      </Field>
      <Field label="Owner">
        <RelationPicker field={f('owner_tech_id', 'technicians')} value={doc.owner_tech_id} onChange={(v) => void onSave({ owner_tech_id: v })} />
      </Field>
      <Field label="Valid until" help="Leave empty for no expiry.">
        <Input type="date" value={expires} onChange={(e) => setExpires(e.target.value)} onBlur={() => { const iso = expires ? new Date(`${expires}T23:59:59Z`).toISOString() : null; if ((doc.expires_at ?? null) !== iso) void onSave({ expires_at: iso }); }} />
      </Field>
    </div>
  );
}

function RichTextAutosave({ value, disabled, ariaLabel, onSave }: { value: string | null; disabled: boolean; ariaLabel: string; onSave: (html: string) => Promise<unknown> }) {
  const last = useRef(value ?? '');
  const timer = useRef<number | null>(null);
  useEffect(() => { last.current = value ?? ''; }, [value]);
  return (
    <RichTextEditor
      value={value}
      disabled={disabled}
      aria-label={ariaLabel}
      onChange={(html) => {
        if (html === last.current) return;
        if (timer.current) window.clearTimeout(timer.current);
        timer.current = window.setTimeout(() => { last.current = html; void onSave(html); }, 800);
      }}
    />
  );
}

// ── sections + lines ──────────────────────────────────────────────────────────
function SectionsEditor({ doc, editable, onChanged }: { doc: Doc; editable: boolean; onChanged: () => void }) {
  const api = useApi();
  const toast = useToast();
  const taxes = useResourceList<{ id: string; name: string; rate_percent: string | null }>('taxes', { limit: 100 });
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }), useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }));
  const fail = (e: unknown, what: string) => toast({ kind: 'error', title: `${what} failed`, detail: e instanceof ApiError ? e.detail : String(e) });
  const call = async (path: string, method: 'POST' | 'PATCH' | 'DELETE', body?: unknown, what = 'Save') => {
    try {
      await api.request(`/v1/quotes/${doc.id}${path}`, { method, body });
      onChanged();
    } catch (e) {
      fail(e, what);
    }
  };
  const onSectionDrag = (e: DragEndEvent) => {
    if (!e.over || e.active.id === e.over.id) return;
    const ids = doc.sections.map((s) => s.id);
    const next = arrayMove(ids, ids.indexOf(String(e.active.id)), ids.indexOf(String(e.over.id)));
    void call('/sections/reorder', 'POST', { ids: next }, 'Reorder');
  };
  const loose = doc.lines.filter((l) => l.section_id === null);
  return (
    <section aria-label="Sections and lines" className="hf-stack">
      <h2 className="hf-section-title">Lines <small>{doc.lines.length} line{doc.lines.length === 1 ? '' : 's'} in {doc.sections.length} section{doc.sections.length === 1 ? '' : 's'}</small></h2>
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onSectionDrag}>
        <SortableContext items={doc.sections.map((s) => s.id)} strategy={verticalListSortingStrategy}>
          {doc.sections.map((s) => (
            <SortableItem key={s.id} id={s.id} disabled={!editable} handleLabel={`Reorder section ${s.title}`}>
              {(handle) => <SectionCard section={s} doc={doc} editable={editable} taxes={taxes.rows} call={call} handle={handle} sensors={sensors} />}
            </SortableItem>
          ))}
        </SortableContext>
      </DndContext>
      {loose.length > 0 && (
        <div className="hf-card pad hf-stack" aria-label="Unsectioned lines">
          <div className="hf-caps">Unsectioned</div>
          <LinesTable doc={doc} lines={loose} editable={editable} taxes={taxes.rows} call={call} sensors={sensors} />
        </div>
      )}
      {editable && (
        <div className="hf-row">
          <Button icon="plus" onClick={() => void call('/sections', 'POST', { title: `Section ${doc.sections.length + 1}` }, 'Add section')}>Add section</Button>
          <AddLine doc={doc} sectionId={null} call={call} label="Add unsectioned line" />
        </div>
      )}
    </section>
  );
}

function SortableItem({ id, disabled, handleLabel, children }: { id: string; disabled: boolean; handleLabel: string; children: (handle: ReactNode) => ReactNode }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id, disabled });
  const handle = disabled ? null : (
    <button type="button" className="hf-iconbtn sm ghost hf-drag-handle" aria-label={handleLabel} title="Drag to reorder (space, then arrows)" {...attributes} {...listeners}>
      <Icon name="grip-vertical" size={14} />
    </button>
  );
  return (
    <div ref={setNodeRef} style={{ transform: CSS.Translate.toString(transform), transition, opacity: isDragging ? 0.7 : 1 }}>
      {children(handle)}
    </div>
  );
}

type Call = (path: string, method: 'POST' | 'PATCH' | 'DELETE', body?: unknown, what?: string) => Promise<void>;
type Sensors = ReturnType<typeof useSensors>;

function SectionCard({ section: s, doc, editable, taxes, call, handle, sensors }: { section: Section; doc: Doc; editable: boolean; taxes: { id: string; name: string; rate_percent: string | null }[]; call: Call; handle: ReactNode; sensors: Sensors }) {
  const [title, setTitle] = useState(s.title);
  useEffect(() => setTitle(s.title), [s.title]);
  const rule = s.selection === 'single' ? 'Customer picks one' : s.selection === 'multi' ? 'Customer picks any' : 'All included';
  return (
    <div className="hf-card pad hf-stack" aria-label={`Section ${s.title}`}>
      <div className="hf-row" style={{ flexWrap: 'wrap' }}>
        {handle}
        {editable ? (
          <Input aria-label="Section title" value={title} onChange={(e) => setTitle(e.target.value)} onBlur={() => title.trim() && title !== s.title && void call(`/sections/${s.id}`, 'PATCH', { title: title.trim() })} style={{ maxWidth: 320 }} />
        ) : (
          <strong>{s.title}</strong>
        )}
        {editable ? (
          <Select aria-label="Selection rule" size="sm" value={s.selection} onChange={(e) => void call(`/sections/${s.id}`, 'PATCH', { selection: e.target.value })} options={[{ value: 'all', label: 'All included' }, { value: 'single', label: 'Customer picks one' }, { value: 'multi', label: 'Customer picks any' }]} />
        ) : (
          <Badge tone={s.selection === 'all' ? 'muted' : 'info'}>{rule}</Badge>
        )}
        {s.selection !== 'all' && (editable ? <Switch label="Section optional" checked={s.is_optional} onChange={(e) => void call(`/sections/${s.id}`, 'PATCH', { is_optional: e.target.checked })} /> : s.is_optional && <Badge tone="muted">optional</Badge>)}
        <span className="hf-grow" />
        {editable && <IconButton icon="trash" label={`Remove section ${s.title}`} variant="ghost" size="sm" onClick={() => void call(`/sections/${s.id}`, 'DELETE', undefined, 'Remove section')} />}
      </div>
      <LinesTable doc={doc} lines={s.lines} editable={editable} taxes={taxes} call={call} sensors={sensors} section={s} />
      {editable && <AddLine doc={doc} sectionId={s.id} call={call} label={`Add line to ${s.title}`} />}
    </div>
  );
}

function LinesTable({ doc, lines, editable, taxes, call, sensors, section }: { doc: Doc; lines: Line[]; editable: boolean; taxes: { id: string; name: string; rate_percent: string | null }[]; call: Call; sensors: Sensors; section?: Section }) {
  const onDrag = (e: DragEndEvent) => {
    if (!e.over || e.active.id === e.over.id) return;
    const ids = lines.map((l) => l.id);
    void call('/lines/reorder', 'POST', { ids: arrayMove(ids, ids.indexOf(String(e.active.id)), ids.indexOf(String(e.over.id))), section_id: section?.id ?? null }, 'Reorder');
  };
  if (lines.length === 0) return <div className="hf-muted" style={{ padding: '6px 0' }}>No lines yet.</div>;
  return (
    <div className="hf-quote-lines" role="table" aria-label={section ? `Lines in ${section.title}` : 'Lines'}>
      <div role="row" className="hf-quote-line head">
        <span />
        <span role="columnheader">Description</span>
        <span role="columnheader" className="num">Qty</span>
        <span role="columnheader" className="num">Unit price</span>
        <span role="columnheader" className="num">Disc %</span>
        <span role="columnheader">Tax</span>
        <span role="columnheader">Billing</span>
        <span role="columnheader">Options</span>
        <span role="columnheader" className="num">Total</span>
        <span />
      </div>
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDrag}>
        <SortableContext items={lines.map((l) => l.id)} strategy={verticalListSortingStrategy}>
          {lines.map((l) => (
            <SortableItem key={l.id} id={l.id} disabled={!editable} handleLabel={`Reorder line ${l.description}`}>
              {(handle) => <LineRow line={l} doc={doc} editable={editable} taxes={taxes} call={call} handle={handle} section={section} />}
            </SortableItem>
          ))}
        </SortableContext>
      </DndContext>
    </div>
  );
}

function LineRow({ line: l, doc, editable, taxes, call, handle, section }: { line: Line; doc: Doc; editable: boolean; taxes: { id: string; name: string; rate_percent: string | null }[]; call: Call; handle: ReactNode; section?: Section }) {
  const [draft, setDraft] = useState({ description: l.description, qty: formatQty(l.qty), discount: String(Number(l.discount_percent) || 0) });
  useEffect(() => setDraft({ description: l.description, qty: formatQty(l.qty), discount: String(Number(l.discount_percent) || 0) }), [l.description, l.qty, l.discount_percent]);
  const patch = (body: Record<string, unknown>) => void call(`/lines/${l.id}`, 'PATCH', body);
  const billing = l.kind === 'recurring' ? (l.recurring_interval ?? 'monthly') : 'one_off';
  const choosable = (section && section.selection !== 'all') || l.is_optional;
  if (!editable) {
    return (
      <div role="row" className={['hf-quote-line', !l.is_selected && 'dropped'].filter(Boolean).join(' ')}>
        <span />
        <span role="cell">{l.description}{l.product_name && l.product_name !== l.description && <span className="hf-muted"> · {l.product_name}</span>}</span>
        <span role="cell" className="num">{formatQty(l.qty)}</span>
        <span role="cell" className="num"><Money minor={l.unit_price_minor} currency={doc.currency} /></span>
        <span role="cell" className="num">{Number(l.discount_percent) ? `${Number(l.discount_percent)}%` : ''}</span>
        <span role="cell">{taxes.find((t) => t.id === l.tax_ref_id)?.name ?? (l.tax_rate_percent ? `${Number(l.tax_rate_percent)}%` : '—')}</span>
        <span role="cell">{billing === 'one_off' ? 'One-time' : INTERVAL_LABEL[billing as Interval]}</span>
        <span role="cell" className="hf-row" style={{ gap: 4 }}>{l.is_optional && <Badge tone="warn">optional</Badge>}{choosable && <Badge tone={l.is_selected ? 'pos' : 'muted'}>{l.is_selected ? 'selected' : 'not selected'}</Badge>}</span>
        <span role="cell" className="num"><Money minor={l.line_total_minor ?? '0'} currency={doc.currency} /></span>
        <span />
      </div>
    );
  }
  return (
    <div role="row" className={['hf-quote-line', !l.is_selected && 'dropped'].filter(Boolean).join(' ')}>
      <span>{handle}</span>
      <span role="cell"><Input aria-label="Description" size="sm" value={draft.description} onChange={(e) => setDraft({ ...draft, description: e.target.value })} onBlur={() => draft.description.trim() && draft.description !== l.description && patch({ description: draft.description.trim() })} /></span>
      <span role="cell" className="num"><Input aria-label="Quantity" size="sm" numeric value={draft.qty} onChange={(e) => setDraft({ ...draft, qty: e.target.value })} onBlur={() => /^\d+(\.\d{1,4})?$/.test(draft.qty) && draft.qty !== formatQty(l.qty) && patch({ qty: draft.qty })} /></span>
      <span role="cell" className="num"><MoneyEditor field={PRICE_FIELD} value={l.unit_price_minor} currency={doc.currency} size="sm" aria-label="Unit price" onChange={(v) => { if (v != null && String(v) !== l.unit_price_minor) patch({ unit_price_minor: String(v) }); }} /></span>
      <span role="cell" className="num"><Input aria-label="Line discount %" size="sm" numeric value={draft.discount} onChange={(e) => setDraft({ ...draft, discount: e.target.value })} onBlur={() => /^\d+(\.\d{1,4})?$/.test(draft.discount) && Number(draft.discount) !== Number(l.discount_percent) && patch({ discount_percent: draft.discount })} /></span>
      <span role="cell"><Select aria-label="Tax" size="sm" value={l.tax_ref_id ?? ''} placeholder="No tax" onChange={(e) => patch({ tax_ref_id: e.target.value || null })} options={taxes.map((t) => ({ value: t.id, label: `${t.name}${t.rate_percent ? ` (${Number(t.rate_percent)}%)` : ''}` }))} /></span>
      <span role="cell"><Select aria-label="Billing" size="sm" value={billing} onChange={(e) => patch(e.target.value === 'one_off' ? { kind: 'one_off', recurring_interval: null } : { kind: 'recurring', recurring_interval: e.target.value })} options={[{ value: 'one_off', label: 'One-time' }, ...INTERVALS.map((i) => ({ value: i, label: INTERVAL_LABEL[i] }))]} /></span>
      <span role="cell" className="hf-row" style={{ gap: 8 }}>
        <Switch aria-label="Optional line" label="opt." checked={l.is_optional} onChange={(e) => patch({ is_optional: e.target.checked })} />
        {choosable && <Switch aria-label="Selected by default" label="sel." checked={l.is_selected} onChange={(e) => patch({ is_selected: e.target.checked })} />}
      </span>
      <span role="cell" className="num"><Money minor={l.line_total_minor ?? '0'} currency={doc.currency} /></span>
      <span><IconButton icon="trash" label={`Remove line ${l.description}`} variant="ghost" size="sm" onClick={() => void call(`/lines/${l.id}`, 'DELETE', undefined, 'Remove line')} /></span>
    </div>
  );
}

function AddLine({ doc, sectionId, call, label }: { doc: Doc; sectionId: string | null; call: Call; label: string }) {
  const api = useApi();
  const load = useCallback(async (q: string): Promise<ComboOption<string>[]> => {
    const page = await api.resource<{ id: string; name: string; sku: string | null; price_minor: string; kind: string; currency: string }>('products').list({ q: q || undefined, limit: 20, sort: 'name', filter: [{ field: 'is_active', op: 'eq', value: true }] });
    return page.data.map((p) => ({ value: p.id, label: p.name, sub: `${p.sku ? `${p.sku} · ` : ''}${p.kind === 'recurring' ? 'recurring' : p.kind}` }));
  }, [api]);
  return (
    <div className="hf-row" style={{ flexWrap: 'wrap' }}>
      <div style={{ minWidth: 260, flex: 1, maxWidth: 420 }}>
        <Combobox<string> value={null} onChange={(v) => v && void call('/lines', 'POST', { product_id: v, section_id: sectionId }, 'Add line')} loadOptions={load} placeholder="Add a product…" aria-label={label} clearable={false} emptyText="No products match" />
      </div>
      <Button size="sm" variant="ghost" icon="plus" onClick={() => void call('/lines', 'POST', { section_id: sectionId, description: 'New line', qty: 1, unit_price_minor: 0 }, 'Add line')}>Blank line</Button>
      <span className="hf-muted" style={{ fontSize: 'var(--t-12)' }}>Prices come from {doc.client_name ?? 'the client'}’s price book.</span>
    </div>
  );
}

// ── totals ────────────────────────────────────────────────────────────────────
function TotalsPanel({ doc, editable, onSave }: { doc: Doc; editable: boolean; onSave: (body: Record<string, unknown>) => Promise<unknown> }) {
  const live = useMemo(() => computeTotals(doc.lines, doc), [doc]);
  const [discount, setDiscount] = useState(String(Number(doc.discount_value) || 0));
  useEffect(() => setDiscount(String(Number(doc.discount_value) || 0)), [doc.discount_value]);
  const cur = doc.currency;
  const buckets: { key: 'one_time' | Interval; label: string; b: typeof live.one_time }[] = [{ key: 'one_time', label: 'One-time', b: live.one_time }, ...INTERVALS.map((i) => ({ key: i, label: `Recurring ${INTERVAL_SHORT[i]}`, b: live.recurring[i] }))];
  const shown = buckets.filter((x, i) => i === 0 || minor(x.b.subtotal_minor) !== 0n);
  return (
    <aside className="hf-quote-totals" aria-label="Totals">
      <div className="hf-caps">Totals</div>
      <div className="hf-form-grid" style={{ marginTop: 8 }}>
        <Field label="Discount">
          <Select size="sm" disabled={!editable} value={doc.discount_type} onChange={(e) => void onSave({ discount_type: e.target.value, discount_value: e.target.value === 'none' ? 0 : Number(discount) || 0 })} options={[{ value: 'none', label: 'None' }, { value: 'percent', label: 'Percent off' }, { value: 'amount', label: 'Amount off (one-time)' }]} />
        </Field>
        <Field label={doc.discount_type === 'amount' ? 'Amount' : 'Percent'}>
          {doc.discount_type === 'amount' ? (
            <MoneyEditor field={PRICE_FIELD} value={doc.discount_value ? String(Math.trunc(Number(doc.discount_value))) : null} currency={cur} size="sm" disabled={!editable} onChange={(v) => v != null && void onSave({ discount_type: 'amount', discount_value: Number(v) })} />
          ) : (
            <Input size="sm" numeric disabled={!editable || doc.discount_type === 'none'} value={discount} onChange={(e) => setDiscount(e.target.value)} onBlur={() => /^\d+(\.\d{1,4})?$/.test(discount) && Number(discount) !== Number(doc.discount_value) && void onSave({ discount_type: doc.discount_type, discount_value: Number(discount) })} suffix="%" />
          )}
        </Field>
        <Field label="Tax" span={2}>
          <Select size="sm" disabled={!editable} value={doc.tax_mode} onChange={(e) => void onSave({ tax_mode: e.target.value })} options={[{ value: 'exclusive', label: 'Tax added on top' }, { value: 'inclusive', label: 'Prices include tax' }, { value: 'none', label: 'No tax' }]} />
        </Field>
      </div>
      {shown.map(({ key, label, b }) => (
        <dl key={key} className="hf-kv hf-quote-bucket" aria-label={label}>
          <div><dt className="hf-caps">{label}</dt><dd /></div>
          <div><dt>Subtotal</dt><dd className="num"><Money minor={b.subtotal_minor} currency={cur} /></dd></div>
          {minor(b.discount_minor) !== 0n && <div><dt>Discount</dt><dd className="num">−<Money minor={b.discount_minor} currency={cur} /></dd></div>}
          {doc.tax_mode !== 'none' && <div><dt>Tax{doc.tax_mode === 'inclusive' ? ' (incl.)' : ''}</dt><dd className="num"><Money minor={b.tax_minor} currency={cur} /></dd></div>}
          <div className="grand"><dt>Total</dt><dd className="num"><strong><Money minor={b.total_minor} currency={cur} /></strong></dd></div>
          <div><dt className="hf-muted">Cost / margin</dt><dd className="num hf-muted"><Money minor={b.cost_minor} currency={cur} /> / <Money minor={b.margin_minor} currency={cur} color /></dd></div>
        </dl>
      ))}
      <div className="hf-muted" style={{ fontSize: 'var(--t-12)', marginTop: 8 }}>{live.selected_line_count} of {live.line_count} lines selected. Totals are recomputed by the server on every change.</div>
    </aside>
  );
}

// ── inspector: timeline, revisions, links ─────────────────────────────────────
function QuoteInspector({ id, doc }: { id: string; doc: Doc }) {
  const api = useApi();
  const [tab, setTab] = useState('events');
  const events = useQuery({ queryKey: ['quote-events', id], queryFn: () => api.request<{ data: { id: string; kind: string; created_at: string; ip: string | null; user_agent: string | null; meta: Record<string, unknown> }[] }>(`/v1/quotes/${id}/events`) });
  const revisions = useQuery({ queryKey: ['quote-revisions', id], queryFn: () => api.request<{ data: { id: string; reason: string; revision: number; created_at: string }[] }>(`/v1/quotes/${id}/revisions`), enabled: tab === 'revisions' });
  return (
    <div className="hf-stack">
      <Tabs variant="pills" tabs={[{ key: 'events', label: 'Timeline', icon: 'history' }, { key: 'revisions', label: 'Revisions', icon: 'git-branch' }, { key: 'links', label: 'Links', icon: 'link' }]} value={tab} onChange={setTab} aria-label="Inspector sections" />
      {tab === 'events' && (
        <ul className="hf-feed">
          {(events.data?.data ?? []).map((e) => (
            <li key={e.id} className="hf-feed-item">
              <span className="glyph" aria-hidden><Icon name="activity" size={13} /></span>
              <div>
              <div className="hf-row" style={{ gap: 8 }}><Badge tone={TONES[e.kind] ?? 'muted'}>{e.kind}</Badge><span className="hf-muted hf-num">{formatDateTime(e.created_at)}</span></div>
              {(e.ip || e.user_agent) && <div className="hf-muted" style={{ fontSize: 'var(--t-12)' }}>{e.ip}{e.user_agent ? ` · ${e.user_agent}` : ''}</div>}
              {e.meta?.signer_name != null && <div style={{ fontSize: 'var(--t-12)' }}>Signed by {String(e.meta.signer_name)} ({String(e.meta.signer_email ?? '')})</div>}
              {e.meta?.reason != null && e.meta.reason !== '' && <div style={{ fontSize: 'var(--t-12)' }}>Reason: {String(e.meta.reason)}</div>}
              {e.meta?.key != null && <div className="hf-muted" style={{ fontSize: 'var(--t-12)' }}>Reminder {String(e.meta.key)}</div>}
              </div>
            </li>
          ))}
          {events.data && events.data.data.length === 0 && <li className="hf-muted">Nothing yet — send the quote to start the timeline.</li>}
        </ul>
      )}
      {tab === 'revisions' && (
        <ul className="hf-feed">
          {(revisions.data?.data ?? []).map((r) => (
            <li key={r.id} className="hf-feed-item"><span className="glyph" aria-hidden><Icon name="git-branch" size={13} /></span><div className="hf-row" style={{ gap: 8 }}><Badge tone="muted">{r.reason}</Badge><span>revision {r.revision}</span><span className="hf-muted hf-num">{formatDateTime(r.created_at)}</span></div></li>
          ))}
          {revisions.data && revisions.data.data.length === 0 && <li className="hf-muted">No snapshots yet.</li>}
        </ul>
      )}
      {tab === 'links' && (
        <ul className="hf-feed">
          {doc.tokens.map((t) => (
            <li key={t.id} className="hf-feed-item">
              <span className="glyph" aria-hidden><Icon name="link" size={13} /></span>
              <div>
                <div className="hf-row" style={{ gap: 8 }}><Badge tone={t.revoked_at ? 'neg' : t.used_at ? 'pos' : 'info'}>{t.revoked_at ? 'revoked' : t.used_at ? 'used' : 'live'}</Badge><span>{t.kind} link</span></div>
                <div className="hf-muted" style={{ fontSize: 'var(--t-12)' }}>{t.sent_to_email ?? 'no recipient'} · expires {formatDate(t.expires_at)}</div>
              </div>
            </li>
          ))}
          {doc.tokens.length === 0 && <li className="hf-muted">No links minted yet.</li>}
        </ul>
      )}
    </div>
  );
}
